/**
 * The checkout return (mission 9 WP12 T076; sheet 08 callout 9; B.1, B.2; FR-013, FR-017's landing,
 * NFR-012; OR-10; squad U1, U24; research §1.28; contracts/ui-api.md "Sheet 08", "Query keys").
 *
 * The provider sends the browser back to `/app/subscribe?checkout=complete` (AuthGate lets an active
 * tenant stay on that URL, OR-10) or `?checkout=cancelled`.
 *
 * - `CheckoutReturn`, on `complete`: the page waits on the tenant's state, never on a timer. It reads
 *   `['tenant']` and `['credits']` every POLL_MS for at most RETURN_WINDOW_MS (NFR-012): the first answer
 *   is the page load's own read, then one read of each every 2 s, 30 of each at most. It lands on
 *   `/app/runs`, replacing the history entry, once the tenant is active **and** its balance is above 0:
 *   the webhook activates the tenant and grants the allowance, and the run list is not the landing until
 *   both have happened (`pending`, `landed`). A webhook that came first lands on the first answer. After
 *   the window, `slow`: Check again reads once more and polls for another window.
 * - `CheckoutCancelled`, on `cancelled`: `checkoutCancelled` above the ready page, which checks out as
 *   before (U24). An abandoned checkout leaves the tenant as it was (amendment 7).
 * The two polls are this screen's own observers over the shared keys, so AuthGate and the credit chip see
 * the same answers and no second read is made for them.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { APP_HOME, tenantQueryOptions } from '@/components/trellis/AuthGate'
import { Band } from '@/components/trellis/Band'
import { Button } from '@/components/ui/button'
import { copyText } from '@/lib/provisional-copy'
import { POLL_MS, creditsQuery } from '@/lib/queries'

/** How long the return polls before it shows the slow state (NFR-012). */
const RETURN_WINDOW_MS = 60_000

export function CheckoutReturn() {
  const navigate = useNavigate()
  const started = useRef(Date.now())
  const [slow, setSlow] = useState(false)

  // The next read is scheduled only while it still falls inside the window: reads at 0, 2, …, 58 s.
  const refetchInterval = (): number | false => (Date.now() - started.current + POLL_MS < RETURN_WINDOW_MS ? POLL_MS : false)
  const tenant = useQuery({ ...tenantQueryOptions, refetchInterval })
  const credits = useQuery({ ...creditsQuery(), refetchInterval })

  const landed = tenant.data?.status === 'active' && (credits.data?.balance ?? 0) > 0
  useEffect(() => {
    if (landed) navigate(APP_HOME, { replace: true })
  }, [landed, navigate])

  useEffect(() => {
    if (slow) return undefined
    const id = setTimeout(() => setSlow(true), RETURN_WINDOW_MS - (Date.now() - started.current))
    return () => clearTimeout(id)
  }, [slow])

  function checkAgain() {
    started.current = Date.now()
    setSlow(false)
    void tenant.refetch()
    void credits.refetch()
  }

  return (
    <div className="flex max-w-[880px] flex-col gap-4">
      <div aria-live="polite" className="flex flex-col gap-2">
        <h1 className="text-h1 text-navy-950">{copyText(slow ? 'checkoutSlowTitle' : 'checkoutPendingTitle')}</h1>
        <p className="text-body text-navy-700">{copyText(slow ? 'checkoutSlowBody' : 'checkoutPendingBody')}</p>
      </div>
      {slow ? (
        <div>
          <Button variant="secondary" onClick={checkAgain}>
            {copyText('checkoutRetry')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

/** `?checkout=cancelled`: nothing was charged, and the page below is ready again (U24). */
export function CheckoutCancelled() {
  return (
    <Band kind="info" requirement="B.1">
      <p>{copyText('checkoutCancelled')}</p>
    </Band>
  )
}
